/**
 * ContactStatusBadge - Color-coded badge for monthly contact compliance status
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Clock, AlertTriangle, AlertCircle, XCircle } from 'lucide-react';

interface ContactStatusBadgeProps {
  status: string;
  size?: 'sm' | 'default';
}

const STATUS_CONFIG: Record<string, {
  label: string;
  variant: string;
  className: string;
  icon: React.ReactNode;
}> = {
  completed: {
    label: 'Completed',
    variant: 'default',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border-green-200',
    icon: <CheckCircle className="w-3 h-3" />,
  },
  on_track: {
    label: 'On Track',
    variant: 'default',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200',
    icon: <Clock className="w-3 h-3" />,
  },
  due: {
    label: 'Due',
    variant: 'default',
    className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border-yellow-200',
    icon: <AlertTriangle className="w-3 h-3" />,
  },
  overdue: {
    label: 'Overdue',
    variant: 'default',
    className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300 border-orange-200',
    icon: <AlertCircle className="w-3 h-3" />,
  },
  critical: {
    label: 'Critical',
    variant: 'destructive',
    className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200',
    icon: <XCircle className="w-3 h-3" />,
  },
};

export const ContactStatusBadge: React.FC<ContactStatusBadgeProps> = ({ status, size = 'default' }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.on_track;

  return (
    <Badge
      variant="outline"
      className={`${config.className} ${size === 'sm' ? 'text-xs px-1.5 py-0' : 'text-xs px-2 py-0.5'} inline-flex items-center gap-1 font-medium`}
    >
      {config.icon}
      {config.label}
    </Badge>
  );
};
